import { FieldPath, FieldValues } from 'react-hook-form'
import { QueryClient } from '@tanstack/react-query'
import { PublicClientApplication } from '@azure/msal-browser'

import { User, UserOrg } from '@/models/user.tsx'
import { CLRM } from '@/models/clrm.tsx'
import { SASS, STFF } from '@/models/stff'
import { FAMI } from '@/models/fami.tsx'
import { CHLD } from '@/models/chld.tsx'
import { FileSubmission, ValidationError } from '@/models/file-submission.tsx'
import { DataResult } from '@progress/kendo-data-query'

export type GlobalStateModel = {
    appUrl: string,
    appVersion: string,
    env: string,
    appInsightsConnectionString: string,
    azureAD: {
        ClientId: string,
        Authority: string,
        TenantId: string,
    },
    queryClient: QueryClient,
    pca: PublicClientApplication,
    msalTokenFn: () => Promise<string>,

    // CAPSDAC data
    coreData: CoreDataModel,
    loginDetail: User<UserOrg>,
    enabledFeatures: string[],
    hasRole: (...roles: string[]) => boolean,
    isStateLevelAdmin: boolean,
    editFilePermissions: () => string[],
    hasFeature: (feature: string) => boolean,
    currentUserOrg: UserOrg | undefined,
    currentOrgPreschools: PreschoolModel[],
    recordTypeLabels: Record<string, string>,
    extractTypeLabels: Record<string, string>
}

export interface EffectiveDateRecord {
    EffectiveStartDate: string
    EffectiveEndDate: string
}

export type DropDownModel = {
    Text: string
    Value: string
}

export type ErrorMessage = {
    ErrorCode: string
    DataFields: string
    ErrorMessagePhase: string
    ErrorMessageType: string
    ErrorMessageText: string
    ErrorMessageDescription: string
}

export type StateModel = {
    Code: string
    CountryCode: string
    StateCode: string
    Name: string
    Description: string
    DisplayOrder: number
}

export type ProgramYearModel = {
    Code: string
    Name: string
    Description: string
    DisplayOrder: number
    EffectiveStartDate: string
    EffectiveEndDate: string
}

export type StatutoryAgeModel = {
    Code: string
    Name: string
    ServiceYear: number
    ServiceMonth: number
    StatutoryOlderStartDate: string
    StatutoryTwoEndDate: string
}

export type CoreDataModel = {
    calWORKsList: DropDownModel[]
    classroomLanguages: DropDownModel[]
    contractTypesOfferedList: DropDownModel[]
    countryList: DropDownModel[]
    educationList: DropDownModel[]
    educationalProgramList: DropDownModel[]
    eligibilityStatusList: DropDownModel[]
    errorMessages: ErrorMessage[]
    errorMessageMap: Map<string, ErrorMessage>
    familySizeList: DropDownModel[]
    genderList: DropDownModel[]
    homelessDwellingTypeCodeList: DropDownModel[]
    IEPorIFSPList: DropDownModel[]
    languageProgramType: DropDownModel[]
    languagesList: DropDownModel[]
    nameSuffixList: DropDownModel[]
    nonClassroomRoleList: DropDownModel[]
    programTypeList: DropDownModel[]
    programDurationList: DropDownModel[]
    programYears: ProgramYearModel[]
    programYearList: DropDownModel[] // Deprecated.  Use programYears
    raceList: DropDownModel[]
    reasonForServiceList: DropDownModel[]
    securityGroup: SecurityGroup[]
    securityRoles: SecurityRole[]
    settingTypesOfferedList: DropDownModel[]
    staffPermitList: DropDownModel[]
    staffRoleList: DropDownModel[]
    statutoryAges: StatutoryAgeModel[]
    states: StateModel[]
    studentExitCategoryList: DropDownModel[]
    timeBaseList: DropDownModel[]
    tooltips: TooltipData[]
    tooltipMap: Map<string, Map<string, string>>
    volunteerFrequencyList: DropDownModel[]
    waiverTypes: DropDownModel[]
    certificationCycle: DropDownModel[]                             
    certificationCycleAnomaly: CertificationCycleAnomalyModel[]    
    reportingCalendar: ReportingCalendarModel[]                      
}

export type SecurityGroup = {
    ScrtyGrpKey: number,
    ScrtyGrpCode: string,
    ScrtyGrpName: string,
    ScrtyGrpDfntn: string,
    ActvFlag: string,
}

export type SecurityRole = {
    CAPSDACRoleId: number,
    CAPSDACRoleCode: string,
    CAPSDACRoleName: string,
    CAPSDACRoleDefinition: string,
    ScrtyGrpKey: number,
    ActvFlag: string,
}

export type TooltipData = {
    FileType: string,
    FieldName: string,
    Description: string,
}

export type CertificationCycleAnomalyModel = {
    CertificationCycleCode: string
    ErrorCode: string
}

export type ReportingCalendarModel = {
    Id: number
    ProgramYearId: number
    CAPSDACsnpshtCode: string
    CAPSDACsnpshtName: string
    CAPSDACsnpshtDfntn: string
    CAPSDACsnpshtType: string
    ActiveFlag: string
    AmendmentEnd: string | null
    AnomalyThresholdPct: number | null
    Deadline: string
    EfctvEndDate: string
    EfctvStartDate: string
    ErrorCertificationCycleCode: string
    WarningCertificationCycleCode: string
    RptngTypeKey: number
    WindowStart: string
}

export type PreschoolModel = {
    PreschoolID: number
    PreschoolCDSCode: string
    PreschoolName: string
    EffectiveStartDate: string
    EffectiveEndDate: string
}

export type LeaModel = {
    LEAID: string
    ESIID: string
    LEAName: string
    CDSCode: string
    VendorNumber: string
    SecurityGroupKey: number
}

export type SCLR = {
    Id: string
    ReportingLEA: string
    PreschoolCode: string
    ProgramYear: string
    LocalStaffId: string
    LocalClassroomId: string
}

export const SCLR_TEMPLATE: SCLR = {
    Id: '',
    ReportingLEA: '',
    PreschoolCode: '',
    ProgramYear: '',
    LocalStaffId: '',
    LocalClassroomId: '',
}

export type ENRL = {
    Id: string
    ReportingLEA: string
    PreschoolCode: string
    ProgramYear: string
    CSPPID: string
    LocalChildId: string
    ContractType: string
    EnrollmentStartDate: string
    EnrollmentEndDate: string
    EnrollmentEndReason: string
    ProgramType: string
    ProgramDuration: string
    //TimeBase: string
    EligibilityStatus: string
}

export const ENRL_TEMPLATE : ENRL = {
    Id: '',
    ReportingLEA: '',
    PreschoolCode: '',
    ProgramYear: '',
    CSPPID: '',
    LocalChildId: '',
    ContractType: '',
    EnrollmentStartDate: '',
    EnrollmentEndDate: '',
    EnrollmentEndReason: '',
    ProgramType: '',
    ProgramDuration: '',
    //TimeBase: '',
    EligibilityStatus: '',
}

export type CLEN = {
    Id: string
    ReportingLEA: string
    PreschoolCode: string
    ProgramYear: string
    CSPPID: string
    LocalChildId: string
    LocalClassroomId: string
    ClassroomEnrollmentStart: string
    ClassroomEnrollmentEnd: string
}

export const CLEN_TEMPLATE : CLEN = {
    Id: '',
    ReportingLEA: '',
    PreschoolCode: '',
    ProgramYear: '',
    CSPPID: '',
    LocalChildId: '',
    LocalClassroomId: '',
    ClassroomEnrollmentStart: '',
    ClassroomEnrollmentEnd: '',
}

export type ATTN = {
    Id: string
    ReportingLEA: string
    PreschoolCode: string
    ProgramYear: string
    LocalFamilyId: string
    CSPPID: string
    LocalChildId: string
    LocalClassroomId: string
    FirstOfMonth: string
    DaysExpected: number
    DaysAttended: number
    DaysAbsentExcused: number
    DaysAbsentUnexcused: number
    DaysAbsentSuspension: number
    DaysAttendedSuspension: number
}

export const ATTN_TEMPLATE : ATTN = {
    Id: '',
    ReportingLEA: '',
    PreschoolCode: '',
    ProgramYear: '',
    LocalFamilyId: '',
    CSPPID: '',
    LocalChildId: '',
    LocalClassroomId: '',
    FirstOfMonth: '',
    DaysExpected: 0,
    DaysAttended: 0,
    DaysAbsentExcused: 0,
    DaysAbsentUnexcused: 0,
    DaysAbsentSuspension: 0,
    DaysAttendedSuspension: 0,
}

export type LANG = {
    Id: string
    ReportingLEA: string
    PreschoolCode: string
    ProgramYear: string
    CSPPID: string
    LocalChildId: string
    InstrumentOptOut: string
    InstrumentDate: string
    InstrumentLanguagesHeard1: string
    InstrumentLanguagesHeard2: string
    InstrumentLanguagesHeard3: string
    InstrumentLanguagesHeard4: string
    InstrumentLanguagesHeard5: string
    InstrumentLanguagesUnderstood1: string
    InstrumentLanguagesUnderstood2: string
    InstrumentLanguagesUnderstood3: string
    InstrumentLanguagesUnderstood4: string
    InstrumentLanguagesUnderstood5: string
    InstrumentLanguagesSpoken1: string
    InstrumentLanguagesSpoken2: string
    InstrumentLanguagesSpoken3: string
    InstrumentLanguagesSpoken4: string
    InstrumentLanguagesSpoken5: string
    DLLInstrumentDesignated: string
    DLLTeacherDesignated: string
    DLLTeacherDesignatedDate: string
} & EffectiveDateRecord

export const LANG_TEMPLATE : LANG = {
    Id: '',
    ReportingLEA: '',
    PreschoolCode: '',
    ProgramYear: '',
    CSPPID: '',
    LocalChildId: '',
    InstrumentOptOut: '',
    InstrumentDate: '',
    InstrumentLanguagesHeard1: '',
    InstrumentLanguagesHeard2: '',
    InstrumentLanguagesHeard3: '',
    InstrumentLanguagesHeard4: '',
    InstrumentLanguagesHeard5: '',
    InstrumentLanguagesUnderstood1: '',
    InstrumentLanguagesUnderstood2: '',
    InstrumentLanguagesUnderstood3: '',
    InstrumentLanguagesUnderstood4: '',
    InstrumentLanguagesUnderstood5: '',
    InstrumentLanguagesSpoken1: '',
    InstrumentLanguagesSpoken2: '',
    InstrumentLanguagesSpoken3: '',
    InstrumentLanguagesSpoken4: '',
    InstrumentLanguagesSpoken5: '',
    DLLInstrumentDesignated: '',
    DLLTeacherDesignated: '',
    DLLTeacherDesignatedDate: '',
    EffectiveStartDate: '',
    EffectiveEndDate: '',
}

export type SLNG = {
    Id: string
    ReportingLEA: string
    PreschoolCode: string
    ProgramYear: string
    CSPPID: string
    LocalChildId: string
    InterviewHomeLanguage1 : string
    InterviewHomeLanguage2: string
    InterviewHomeLanguage3: string
    InterviewHomeLanguage4: string
    InterviewHomeLanguage5: string
    InterviewMostUsedLanguage: string
} & EffectiveDateRecord

export const SLNG_TEMPLATE : SLNG = {
    Id: '',
    ReportingLEA: '',
    PreschoolCode: '',
    ProgramYear: '',
    CSPPID: '',
    LocalChildId: '',
    InterviewHomeLanguage1: '',
    InterviewHomeLanguage2: '',
    InterviewHomeLanguage3: '',
    InterviewHomeLanguage4: '',
    InterviewHomeLanguage5: '',
    InterviewMostUsedLanguage: '',
    EffectiveStartDate: '',
    EffectiveEndDate: '',
}

export type PRGM = {
    ReportingLEA: string
    PreschoolCode: string
    ProgramYear: string
    CSPPID: string
    LocalChildId: string
    EduProgramCode: string
    EduProgramMembershipStartDate: string
    EduProgramMembershipEndDate: string
    HomelessDwellingTypeCode: string
    UnaccompaniedYouthIndicator: string
}

export const PRGM_TEMPLATE : PRGM = {
    ReportingLEA: '',
    PreschoolCode: '',
    ProgramYear: '',
    CSPPID: '',
    LocalChildId: '',
    EduProgramCode: '',
    EduProgramMembershipStartDate: '',
    EduProgramMembershipEndDate: '',
    HomelessDwellingTypeCode: '',
    UnaccompaniedYouthIndicator: '',
}

export type EffectiveDateEntity<RecordType> = {
    records: (RecordType & EffectiveDateRecord)[]
}

export type EntitiesMap<RecordType> = {
    [key: string]: EffectiveDateEntity<RecordType>
}

export type EntityModel = {
    children: EntitiesMap<CHLD>
    families: EntitiesMap<FAMI>
    classrooms: EntitiesMap<CLRM>
    staff: EntitiesMap<STFF>
}

export type LeaDataModel = {
    clrm: Array<CLRM>
    chld: Array<CHLD>
    fami: Array<FAMI>
    stff: Array<STFF>
    sass: Array<SASS>
    enrl: Array<ENRL>
    sclr: Array<SCLR>
    clen: Array<CLEN>
    attn: Array<ATTN>
    lang: Array<LANG>
    slng: Array<SLNG>
    prgm: Array<PRGM>
    entities: EntityModel
}

export type LeaTestDataModel = {
    lea: LeaModel
    preschools: Array<PreschoolModel>
} & LeaDataModel

export type ChildDataModel = {
    chld: Array<CHLD>
    fami: Array<FAMI>
    enrl: Array<ENRL>
    clen: Array<CLEN>
    lang: Array<LANG>
    slng: Array<SLNG>
    prgm: Array<PRGM>
}

export const defaultLeaData: LeaDataModel = {
    clrm: [],
    chld: [],
    fami: [],
    stff: [],
    sass: [],
    enrl: [],
    sclr: [],
    clen: [],
    attn: [],
    prgm: [],
    entities: null
}

export type MutationResponse<T extends FieldValues> = {
    fileSubmission: FileSubmission | null,
    errors: ValidationError<FieldPath<T>>[],
    serverError?: string,
}

export class ServerError {
    message: string

    constructor(message: string) {
        this.message = message
    }
}

export class ResultPendingError {
    message: string

    constructor(message: string) {
        this.message = message
    }
}

export const recordTypes = [
    'CHLD',
    'FAMI',
    'STFF',
    'SASS',
    'CLRM',
    'SCLR',
    'ENRL',
    'CLEN',
    'ATTN',
    'LANG',
    'SLNG',
    'PRGM',
] as const
export const fileUploadRecordTypes = [
  ...recordTypes,
  // CSPPIDCandidate is the "record type" used for the CSPPID Match Tool
  'CSPPIDCandidate'
]
export const extractRecordTypes = [
    ...recordTypes,
    // Preschool and CSPPID are two extract-specific types
    'Preschool',
    'CSPPID'
]
export const roleRecordTypes = [
  ...recordTypes,
  // ELAS and SPED records are CALPADS-imports and only for state-level roles
  'ELAS',
  'SPED'
]
export type RecordType = typeof recordTypes[number]

export type FileEntry = {
    file: File;
    recordType: RecordType | '';
    isHeaderRow?: boolean
}

export type SearchResult<T> = {
  matchRecord: T,
  score: number
}

export type TypedDataResult<T> = {
    data: T[],
} & DataResult

export type ReportItem = {
  reportId: number;
  name: string;
  serverURL: string;
  reportType: string;
  extendedName: string;
  isTemplate: boolean;
  description: string | null;
};

export type MessageType = "Certification" | "Maintenance" | "CAPSDAC Update" | "Action Required";
export type MessageStatus = "Live" | "Scheduled" | "Past";
export interface SystemMessage {
  id: string;
  messageType: MessageType;
  title: string;
  body: string;
  highPriority: boolean;
  global: boolean;
  leaIds?: string[];
  startDate: string; // ISO string
  endDate: string;
  active: boolean;
  sortOrder: number;
}
